/* eslint-disable no-undef */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from "react";
import "./css/StaffPage.css";
import { getAllPendingIncentives, markDriverIncentivePaid, getAllPayoutHistory } from "../api/driverReferralAdminApi";
import {
  FiSearch,
  FiX,
  FiCheckCircle,
  FiClock,
  FiList,
} from "react-icons/fi";
import { BsCurrencyRupee } from "react-icons/bs";
import AccessControl from "../components/AccessControl";

const ADMIN_PANEL_ROLES = ["SUPERADMIN", "ADMIN", "HR"];

export default function DriverReferralPayoutsPage() {
  const [activeTab, setActiveTab] = useState("pending"); // "pending" | "history"

  const [drivers, setDrivers] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [toast, setToast] = useState({ show: false, message: "" });

  // ── Paid history ──────────────────────────────────────────────────────
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [modeFilter, setModeFilter] = useState(""); // "" | "UPI" | "CASH" | "BANK_TRANSFER"

  // ── Mark Paid modal ───────────────────────────────────────────────────
  const [payoutModal, setPayoutModal] = useState(null); // holds the driver row being paid
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("UPI");
  const [transactionRef, setTransactionRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const showToast = (message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: "" }), 2500);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllPendingIncentives();
      if (res.success) {
        setDrivers(res.drivers || []);
        setTotalPending(res.totalPending || 0);
      } else {
        setDrivers([]);
      }
    } catch {
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await getAllPayoutHistory();
      if (res.success) {
        setPayoutHistory(res.payouts || []);
        setTotalPaidAmount(res.totalPaidAmount || 0);
      } else {
        setPayoutHistory([]);
      }
    } catch {
      setPayoutHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchHistory();
  }, [fetchData, fetchHistory]);

  const filtered = drivers.filter((d) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      d.name?.toLowerCase().includes(q) ||
      d.phone?.toLowerCase().includes(q) ||
      d.driverInviteCode?.toLowerCase().includes(q)
    );
  });

  const filteredHistory = payoutHistory.filter((p) => {
    if (modeFilter && p.paymentMode !== modeFilter) return false;
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      p.driverName?.toLowerCase().includes(q) ||
      p.driverPhone?.toLowerCase().includes(q) ||
      p.driverInviteCode?.toLowerCase().includes(q) ||
      p.transactionRef?.toLowerCase().includes(q)
    );
  });

  const openPayoutModal = (driver) => {
    setPayoutModal(driver);
    setAmount(driver.pendingAmount.toString());
    setPaymentMode("UPI");
    setTransactionRef("");
    setRemarks("");
  };

  const closePayoutModal = () => setPayoutModal(null);

  const submitPayout = async () => {
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      showToast("Enter a valid amount");
      return;
    }
    if (parseFloat(amount) > payoutModal.pendingAmount) {
      showToast(`Amount can't exceed pending ₹${payoutModal.pendingAmount}`);
      return;
    }

    setSubmitting(true);
    try {
      const res = await markDriverIncentivePaid(payoutModal.driverId, {
        amount: parseFloat(amount),
        paymentMode,
        transactionRef,
        remarks,
      });
      if (res.success) {
        showToast(res.message || "Marked as paid!");
        closePayoutModal();
        fetchData();
        fetchHistory();
      } else {
        showToast(res.error || "Failed to mark as paid");
      }
    } catch {
      showToast("Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <AccessControl allowedRoles={ADMIN_PANEL_ROLES} redirectTo="/" />

      {toast.show && (
        <div className="sf-toast sf-toast-success">
          <span>{toast.message}</span>
        </div>
      )}

      {/* Header */}
      <div className="sf-page-header section">
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a", margin: 0 }}>
            Driver Referral Payouts
          </h2>
          <p style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            Pending "Refer a Driver" incentives (₹100 per driver's first ride)
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="stat-grid section">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#FAEEDA" }}>
            <FiClock size={18} color="#854F0B" />
          </div>
          <div className="stat-label">Drivers Awaiting Payout</div>
          <div className="stat-value">{drivers.length}</div>
          <div className="stat-change neutral">Have unpaid incentive</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#FCEBEB" }}>
            <BsCurrencyRupee size={16} color="#A32D2D" />
          </div>
          <div className="stat-label">Total Pending Amount</div>
          <div className="stat-value" style={{ color: "#A32D2D" }}>₹{totalPending}</div>
          <div className="stat-change neutral">To be transferred</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: "#E1F5EE" }}>
            <FiCheckCircle size={18} color="#0F6E56" />
          </div>
          <div className="stat-label">Total Paid Out</div>
          <div className="stat-value" style={{ color: "#0F6E56" }}>₹{totalPaidAmount}</div>
          <div className="stat-change neutral">{payoutHistory.length} payout(s) so far</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card section" style={{ padding: "6px", display: "flex", gap: 6 }}>
        <button
          onClick={() => setActiveTab("pending")}
          className="btn"
          style={{
            flex: 1,
            background: activeTab === "pending" ? "#1d9e75" : "transparent",
            color: activeTab === "pending" ? "#fff" : "#555",
            fontWeight: 600,
          }}
        >
          <FiClock size={14} style={{ marginRight: 6 }} />
          Pending ({drivers.length})
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className="btn"
          style={{
            flex: 1,
            background: activeTab === "history" ? "#1d9e75" : "transparent",
            color: activeTab === "history" ? "#fff" : "#555",
            fontWeight: 600,
          }}
        >
          <FiList size={14} style={{ marginRight: 6 }} />
          Paid History ({payoutHistory.length})
        </button>
      </div>

      {/* Toolbar */}
      <div className="card section">
        <div className="sf-toolbar">
          <div className="search-box">
            <FiSearch size={14} className="search-icon" />
            <input
              placeholder={
                activeTab === "pending"
                  ? "Search driver name, phone or invite code…"
                  : "Search driver name, phone, invite code or transaction ref…"
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="search-clear" onClick={() => setSearchTerm("")}>
                <FiX size={12} />
              </button>
            )}
          </div>
          {activeTab === "history" && (
            <select
              value={modeFilter}
              onChange={(e) => setModeFilter(e.target.value)}
              style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", fontSize: 12 }}
            >
              <option value="">All Payment Modes</option>
              <option value="UPI">UPI</option>
              <option value="CASH">Cash</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
            </select>
          )}
        </div>
      </div>

      {/* Pending Table */}
      {activeTab === "pending" && (
      <div className="card section">
        {loading ? (
          <div className="sf-loading" style={{ height: "40vh" }}>
            <div
              className="spinner"
              style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }}
            ></div>
            <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading pending payouts…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="sf-empty">
            <FiCheckCircle size={40} color="#ddd" />
            <p>No pending referral incentive payouts — everyone's settled up 🎉</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Driver</th>
                  <th>UPI ID</th>
                  <th>Total Earned</th>
                  <th>Already Paid</th>
                  <th>Pending</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => (
                  <tr key={d.driverId}>
                    <td>
                      <div className="sf-member-cell">
                        <div className="sf-avatar">{d.name?.charAt(0)?.toUpperCase() || "D"}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{d.name || "—"}</div>
                          <div style={{ fontSize: 11, color: "#888" }}>{d.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: "#555", fontFamily: "monospace" }}>
                      {d.upiId || "—"}
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 600 }}>₹{d.totalReferralIncentive}</td>
                    <td style={{ fontSize: 13, color: "#0F6E56" }}>₹{d.incentivePaidAmount}</td>
                    <td style={{ fontSize: 13, fontWeight: 700, color: "#A32D2D" }}>
                      ₹{d.pendingAmount}
                    </td>
                    <td>
                      <button className="btn btn-success" onClick={() => openPayoutModal(d)}>
                        Mark Paid
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Paid History Table */}
      {activeTab === "history" && (
      <div className="card section">
        {historyLoading ? (
          <div className="sf-loading" style={{ height: "40vh" }}>
            <div
              className="spinner"
              style={{ width: 24, height: 24, borderColor: "#1d9e75", borderTopColor: "transparent" }}
            ></div>
            <p style={{ marginTop: 12, color: "#888", fontSize: 13 }}>Loading payout history…</p>
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="sf-empty">
            <FiList size={40} color="#ddd" />
            <p>No payouts recorded yet</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Driver</th>
                  <th>Amount</th>
                  <th>Mode</th>
                  <th>Transaction Ref</th>
                  <th>Paid On</th>
                  <th>Paid By</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((p) => (
                  <tr key={p.payoutId}>
                    <td>
                      <div className="sf-member-cell">
                        <div className="sf-avatar">{p.driverName?.charAt(0)?.toUpperCase() || "D"}</div>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{p.driverName}</div>
                          <div style={{ fontSize: 11, color: "#888" }}>{p.driverPhone}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 700, color: "#0F6E56" }}>₹{p.amount}</td>
                    <td>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          padding: "4px 10px",
                          borderRadius: 20,
                          color: "#185FA5",
                          background: "#E6F1FB",
                        }}
                      >
                        {p.paymentMode === "BANK_TRANSFER" ? "Bank Transfer" : p.paymentMode}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: "#555", fontFamily: "monospace" }}>
                      {p.transactionRef || "—"}
                    </td>
                    <td style={{ fontSize: 12, color: "#555" }}>
                      {new Date(p.paidOn).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                    </td>
                    <td style={{ fontSize: 12, color: "#555" }}>{p.paidByName}</td>
                    <td style={{ fontSize: 12, color: "#888" }}>{p.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* ── Mark Paid Modal ─────────────────────────────────────────────── */}
      {payoutModal && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }}
          onClick={closePayoutModal}
        >
          <div
            className="card"
            style={{ width: 380, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
              Mark Paid — {payoutModal.name}
            </h3>
            <p style={{ fontSize: 12, color: "#888", marginBottom: 16 }}>
              Pending: ₹{payoutModal.pendingAmount} · UPI: {payoutModal.upiId || "not set"}
            </p>

            <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", marginTop: 4, marginBottom: 12, borderRadius: 8, border: "1px solid #ddd" }}
            />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Payment Mode</label>
            <select
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", marginTop: 4, marginBottom: 12, borderRadius: 8, border: "1px solid #ddd" }}
            >
              <option value="UPI">UPI</option>
              <option value="CASH">Cash</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
            </select>

            <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Transaction Ref (optional)</label>
            <input
              value={transactionRef}
              onChange={(e) => setTransactionRef(e.target.value)}
              placeholder="UPI txn ID, cheque no., etc."
              style={{ width: "100%", padding: "8px 10px", marginTop: 4, marginBottom: 12, borderRadius: 8, border: "1px solid #ddd" }}
            />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#555" }}>Remarks (optional)</label>
            <input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", marginTop: 4, marginBottom: 20, borderRadius: 8, border: "1px solid #ddd" }}
            />

            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn"
                style={{ flex: 1, background: "#f3f4f6" }}
                onClick={closePayoutModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                style={{ flex: 1 }}
                onClick={submitPayout}
                disabled={submitting}
              >
                {submitting ? "Saving…" : "Confirm Paid"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}